import { UpdateWorkspaceSettingsMutation, WorkspaceSettingsQuery } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { ChartPaletteConfig } from "@ark/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DashboardOverviewConfig } from "../../types/dashboard-overview-config";
import { TerminologyConfig } from "../use-workspace-terminology";

import { updateWorkspaceSettingsMutationDocument, workspaceSettingsQueryDocument } from "./workspace-settings.gql";

export interface UseWorkspaceSettingsProps {
  readonly workspaceId: string;
  readonly enabled?: boolean;
}

type WorkspaceSettingsMutationVariables = {
  workspaceId: string;
  chartPaletteId?: string;
  paletteConfig?: ChartPaletteConfig;
  terminologyConfig?: TerminologyConfig | null;
  terminologyLocale?: string;
  dashboardOverviewConfig?: DashboardOverviewConfig;
  dashboardNavigationConfig?: Record<string, unknown>;
  locale?: string;
};

type WorkspaceSettingsMutationContext = {
  previousData: WorkspaceSettingsQuery | undefined;
};

export const useWorkspaceSettings = ({ workspaceId, enabled = true }: UseWorkspaceSettingsProps) => {
  const queryClient = useQueryClient();

  const queryResult = useQuery<WorkspaceSettingsQuery>({
    queryKey: queryKeys.workspaces.settings(workspaceId),
    queryFn: async () => {
      const client = createOperationClient("workspaceSettings");
      return await client.request(workspaceSettingsQueryDocument, {
        input: { workspaceId },
      });
    },
    enabled: enabled && !!workspaceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const mutation = useMutation<
    UpdateWorkspaceSettingsMutation,
    Error,
    WorkspaceSettingsMutationVariables,
    WorkspaceSettingsMutationContext
  >({
    mutationFn: async (input) => {
      const client = createOperationClient("updateWorkspaceSettings");
      // GraphQLJSONObject scalar type expects an object, not a string
      // The GraphQL client will handle serialization automatically
      return await client.request(updateWorkspaceSettingsMutationDocument, {
        input: {
          workspaceId: input.workspaceId,
          chartPaletteId: input.chartPaletteId,
          paletteConfig: input.paletteConfig,
          terminologyConfig: input.terminologyConfig,
          terminologyLocale: input.terminologyLocale,
          dashboardOverviewConfig: input.dashboardOverviewConfig,
          dashboardNavigationConfig: input.dashboardNavigationConfig,
          locale: input.locale,
        },
      });
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.workspaces.settings(variables.workspaceId) });
      const previousData = queryClient.getQueryData<WorkspaceSettingsQuery>(
        queryKeys.workspaces.settings(variables.workspaceId),
      );
      if (previousData) {
        queryClient.setQueryData<WorkspaceSettingsQuery>(queryKeys.workspaces.settings(variables.workspaceId), {
          workspaceSettings: {
            ...previousData.workspaceSettings,
            ...(variables.chartPaletteId !== undefined && { chartPaletteId: variables.chartPaletteId }),
            ...(variables.paletteConfig !== undefined && { paletteConfig: variables.paletteConfig }),
            ...(variables.terminologyConfig !== undefined && { terminologyConfig: variables.terminologyConfig }),
            ...(variables.dashboardOverviewConfig !== undefined && {
              dashboardOverviewConfig: variables.dashboardOverviewConfig,
            }),
            ...(variables.dashboardNavigationConfig !== undefined && {
              dashboardNavigationConfig: variables.dashboardNavigationConfig,
            }),
            ...(variables.locale !== undefined && { locale: variables.locale }),
          },
        });
      }
      return { previousData };
    },
    onError: (_err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKeys.workspaces.settings(variables.workspaceId), context.previousData);
      }
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<WorkspaceSettingsQuery>(queryKeys.workspaces.settings(variables.workspaceId), {
        workspaceSettings: data.updateWorkspaceSettings,
      });
    },
  });

  return {
    data: queryResult.data?.workspaceSettings ?? null,
    loading: queryResult.isPending,
    error: queryResult.error,
    refetch: queryResult.refetch,
    updateSettings: mutation.mutate,
    updateSettingsAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    updateError: mutation.error,
  };
};
